'use client';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { hasRichTextContent } from '../../../utils/richText';
import { CollapsibleSection } from '../../CollapsibleSection/CollapsibleSection';
import { RawHtml } from '../../RawHtml/RawHtml';
import { AioModuleTracking, sendAioNavigate } from '../tracking';
import { AioFaqItem } from '../types';

type FaqAioModuleProps = {
  faqName?: string;
  items: AioFaqItem[];
} & AioModuleTracking;

export const FaqAioModule = ({
  faqName,
  items,
  pushDataLayer,
  componentIndex,
}: FaqAioModuleProps): JSX.Element | null => {
  const renderableItems = items.filter(
    (item) => item.question?.trim() && hasRichTextContent(item.answer),
  );

  const [openId, setOpenId] = useState<string>(
    () => renderableItems[0]?.id ?? '',
  );

  const listRef = useRef<HTMLUListElement>(null);

  /**
   * #2538 - links inside an answer. The answers are editor rich text, so there
   * is no React element to hang an `onClick` on; one delegated listener on the
   * list covers every answer, the same way `PieceOfContent` tracks inline links
   * in a Text Block. The row's position comes from the `<li>` the link sits in,
   * which keeps question and link on the same index.
   */
  useEffect(() => {
    const list = listRef.current;
    if (!list || !pushDataLayer) return undefined;

    const handleClick = (nativeEvent: MouseEvent) => {
      const target = nativeEvent.target as HTMLElement | null;
      const link = target?.closest?.('a');
      if (!link || !list.contains(link)) return;

      const href = (link.getAttribute('href') ?? '').trim();
      if (!href || href.toLowerCase().startsWith('javascript:')) return;

      const row = link.closest('li');
      if (!row) return;
      const index = Array.prototype.indexOf.call(list.children, row);

      // Collapse internal whitespace: an anchor spanning several lines of the
      // raw HTML would otherwise carry newlines into the title.
      const clickText = (link.textContent ?? '').replace(/\s+/g, ' ').trim();

      sendAioNavigate(
        { pushDataLayer, componentIndex },
        {
          componentType: 'text-link',
          title: `FAQ Accordion_${clickText}`,
          action: 'click',
          index: Math.max(0, index),
        },
      );
    };

    list.addEventListener('click', handleClick);
    return () => list.removeEventListener('click', handleClick);
  }, [pushDataLayer, componentIndex]);

  if (!renderableItems.length) {
    return null;
  }

  const heading = faqName?.trim();

  return (
    <section
      data-component="aio-module-faq"
      className="flex flex-col gap-spacing/gap/soft-12px"
    >
      {heading ? (
        <h3 className="mhg-alpha-bullet-2 text-text/secondary-lighter">
          {heading}
        </h3>
      ) : null}

      <ul ref={listRef} className="flex flex-col gap-spacing/gap/soft-12px">
        {renderableItems.map((item, index) => (
          <li key={item.id}>
            <CollapsibleSection
              id={item.id}
              isOpen={openId === item.id}
              onToggle={(nextId) => {
                // The primitive passes '' when the open row is closed, the id
                // when a row is opened - so the action reads off the argument
                // rather than the state we are about to set (#2537).
                sendAioNavigate(
                  { pushDataLayer, componentIndex },
                  {
                    componentType: 'faq',
                    title: `FAQ Accordion_${item.question?.trim() ?? ''}`,
                    action: nextId ? 'expand' : 'collapse',
                    index,
                  },
                );
                setOpenId(nextId);
              }}
              buttonClassName={classNames('!h-auto', {
                '!bg-fill/alternative-1': openId !== item.id,
              })}
              content={
                <RawHtml
                  baseTextStyle="none"
                  className="px-spacing/padding/x py-spacing/padding/y-sm text-text/primary mhg-alpha-typography-body-1-blog"
                  content={item.answer}
                />
              }
            >
              {item.question}
            </CollapsibleSection>
          </li>
        ))}
      </ul>
    </section>
  );
};
